import { create } from 'zustand';
import { bookingListApi } from '@api/api.ts';
import type { AppBookingExportRequest, AppBookingListItemDto, AppBookingSummaryDto } from '@api/generated';

export interface HistoryFilters {
  dateFrom?: string;
  dateTo?: string;
  query?: string;
  state?: string;
  salonId?: string;
  workerId?: string;
  customerId?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface HistoryState {
  // Data
  bookings: AppBookingListItemDto[];
  summary: AppBookingSummaryDto | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
  } | null;

  // UI state
  loading: boolean;
  error: string | null;
  filters: HistoryFilters;

  // Actions
  loadBookings: () => Promise<void>;
  loadBookingsWithFilters: (filters: Partial<HistoryFilters>) => Promise<void>;
  loadSummary: (filters?: Partial<HistoryFilters>) => Promise<void>;
  setFilters: (filters: Partial<HistoryFilters>) => void;
  resetFilters: () => void;

  // Export
  exportBookings: (request: AppBookingExportRequest) => Promise<string>;
  quickExportBookings: (filters?: Partial<HistoryFilters>, format?: string) => Promise<string>;

  // Pagination
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;

  clearError: () => void;
}

const defaultFilters: HistoryFilters = {
  page: 0,
  size: 10,
  sortBy: 'start',
  sortOrder: 'desc',
};

export const useHistoryStore = create<HistoryState>((set, get) => ({
  // Initial state
  bookings: [],
  summary: null,
  pagination: null,
  loading: false,
  error: null,
  filters: defaultFilters,

  // Load bookings with current filters
  loadBookings: async () => {
    set({ loading: true, error: null });

    try {
      const currentFilters = get().filters;

        const response = await bookingListApi.getBookingList({
          dateFrom: currentFilters.dateFrom,
          dateTo: currentFilters.dateTo,
          query: currentFilters.query,
          state: currentFilters.state,
          salonId: currentFilters.salonId,
          workerId: currentFilters.workerId,
          customerId: currentFilters.customerId,
          page: currentFilters.page,
          size: currentFilters.size,
          sortBy: currentFilters.sortBy,
          sortOrder: currentFilters.sortOrder
        });

      if (response.status === 200 && response.data) {
        set({
          bookings: response.data.bookings || [],
          pagination: response.data.pagination
            ? {
                page: response.data.pagination.page || 0,
                size: response.data.pagination.limit || 20,
                totalElements: response.data.pagination.total || 0,
                totalPages: response.data.pagination.totalPages || 0,
              }
            : null,
          loading: false,
        });
      } else {
        set({ error: 'Nepodařilo se načíst seznam rezervací', loading: false });
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      set({
        error: error instanceof Error ? error.message : 'Nepodařilo se načíst seznam rezervací',
        loading: false,
      });
    }
  },

  // Load bookings with new filters
  loadBookingsWithFilters: async (newFilters: Partial<HistoryFilters>) => {
    set({ loading: true, error: null });

    try {
      const currentFilters = get().filters;
      const filters = { ...currentFilters, ...newFilters };

      // Update filters in state
      set({ filters });

        const response = await bookingListApi.getBookingList({
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
          state: filters.state,
          salonId: filters.salonId,
          workerId: filters.workerId,
          customerId: filters.customerId,
          page: filters.page,
          size: filters.size,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder
        });

      if (response.status === 200 && response.data) {
        set({
          bookings: response.data.bookings || [],
          pagination: response.data.pagination
            ? {
                page: response.data.pagination.page || 0,
                size: response.data.pagination.limit || 20,
                totalElements: response.data.pagination.total || 0,
                totalPages: response.data.pagination.totalPages || 0,
              }
            : null,
          loading: false,
        });
      } else {
        set({ error: 'Nepodařilo se načíst seznam rezervací', loading: false });
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      set({
        error: error instanceof Error ? error.message : 'Nepodařilo se načíst seznam rezervací',
        loading: false,
      });
    }
  },

  // Load summary
  loadSummary: async (newFilters?: Partial<HistoryFilters>) => {
    try {
      const currentFilters = get().filters;
      const filters = { ...currentFilters, ...newFilters };

        const response = await bookingListApi.getBookingSummary1({
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          query: filters.query,
          state: filters.state,
          salonId: filters.salonId,
          workerId: filters.workerId,
          customerId: filters.customerId
        });

      if (response.status === 200 && response.data) {
        set({ summary: response.data });
      }
    } catch (error) {
      console.error('Error loading summary:', error);
      // Summary error is not critical, don't set main error state
    }
  },

  // Set filters
  setFilters: (newFilters: Partial<HistoryFilters>) => {
    const currentFilters = get().filters;
    const updatedFilters = { ...currentFilters, ...newFilters };
    set({ filters: updatedFilters });
  },

  // Reset filters to default
  resetFilters: () => {
    set({ filters: defaultFilters });
  },

  // Export bookings
  exportBookings: async (request: AppBookingExportRequest) => {
    try {
      const response = await bookingListApi.exportBookings({
        appBookingExportRequest: request
      });
      if (response.status === 200 && response.data) {
        return response.data;
      }
      throw new Error('Export se nezdařil');
    } catch (error) {
      console.error('Error exporting bookings:', error);
      throw error;
    }
  },

  // Quick export bookings
  quickExportBookings: async (filters?: Partial<HistoryFilters>, format: string = 'CSV') => {
    try {
      const currentFilters = get().filters;
      const exportFilters = { ...currentFilters, ...filters };

      const response = await bookingListApi.quickExportBookings({
        dateFrom: exportFilters.dateFrom,
        dateTo: exportFilters.dateTo,
        query: exportFilters.query,
        state: exportFilters.state,
        salonId: exportFilters.salonId,
        workerId: exportFilters.workerId,
        customerId: exportFilters.customerId,
        format: format
      });

      if (response.status === 200 && response.data) {
        return response.data;
      }
      throw new Error('Export se nezdařil');
    } catch (error) {
      console.error('Error quick exporting bookings:', error);
      throw error;
    }
  },

  // Pagination actions
  goToPage: (page: number) => {
    const currentFilters = get().filters;
    // UniversalPaginationCallback používá 1-based stránkování, API používá 0-based
    set({ filters: { ...currentFilters, page: page - 1 } });
  },

  changePageSize: (size: number) => {
    const currentFilters = get().filters;
    set({ filters: { ...currentFilters, size, page: 0 } });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
